import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Trophy, Clock, CheckSquare, DollarSign, Users, Star, 
  TrendingUp, Zap, Award, RefreshCw, Calendar
} from 'lucide-react';

const BADGE_ICONS = {
  'trophy': Trophy,
  'clock': Clock,
  'check-square': CheckSquare,
  'check-circle': CheckSquare,
  'calendar-check': Calendar,
  'dollar-sign': DollarSign,
  'users': Users,
  'star': Star,
  'trending-up': TrendingUp,
  'zap': Zap,
  'award': Award,
  'refresh-cw': RefreshCw
};

const BADGE_COLORS = {
  'gold': 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white',
  'purple': 'bg-gradient-to-br from-purple-400 to-purple-600 text-white',
  'blue': 'bg-gradient-to-br from-blue-400 to-blue-600 text-white',
  'green': 'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white',
  'teal': 'bg-gradient-to-br from-teal-400 to-teal-600 text-white',
  'orange': 'bg-gradient-to-br from-orange-400 to-orange-600 text-white',
  'emerald': 'bg-gradient-to-br from-emerald-400 to-green-600 text-white',
  'amber': 'bg-gradient-to-br from-amber-400 to-amber-600 text-white',
  'yellow': 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white',
  'lime': 'bg-gradient-to-br from-lime-400 to-lime-600 text-white',
  'indigo': 'bg-gradient-to-br from-indigo-400 to-indigo-600 text-white',
  'pink': 'bg-gradient-to-br from-pink-400 to-pink-600 text-white',
  'gradient-gold': 'bg-gradient-to-br from-yellow-300 via-amber-400 to-orange-500 text-white'
};

const BadgeCard = ({ badge, size = "normal" }) => {
  const IconComponent = BADGE_ICONS[badge.icon] || Award;
  const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS['blue'];
  
  const sizeClasses = size === "small" 
    ? "w-12 h-12 text-lg" 
    : "w-16 h-16 text-2xl";
  
  return (
    <div className="flex flex-col items-center text-center p-3" data-testid={`badge-${badge.id}`}>
      <div className={`${sizeClasses} rounded-full ${colorClass} flex items-center justify-center shadow-lg mb-2`}>
        <IconComponent className={size === "small" ? "w-6 h-6" : "w-8 h-8"} />
      </div>
      <p className="font-semibold text-sm">{badge.name_ro || badge.name}</p>
      {size !== "small" && (
        <p className="text-xs text-muted-foreground mt-1">{badge.description}</p>
      )}
    </div>
  );
};

const BadgesList = ({ badges, title = "Badge-uri Câștigate" }) => {
  if (!badges || badges.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <Trophy className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
          <p className="text-muted-foreground">Încă nu ai câștigat badge-uri</p>
          <p className="text-sm text-muted-foreground mt-1">Continuă să îți îmbunătățești performanța!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          {title}
          <Badge variant="secondary" className="ml-2">{badges.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {badges.map((badge, idx) => (
            <BadgeCard key={badge.id || idx} badge={badge} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const Leaderboard = ({ month }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await api.get(`/badges/leaderboard?month=${month}`);
        setLeaderboard(response.data);
      } catch (err) {
        console.error('Error fetching leaderboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [month]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-500" />
          Clasament Badge-uri
        </CardTitle>
      </CardHeader>
      <CardContent>
        {leaderboard.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">
            Niciun badge acordat încă pentru această lună
          </p>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((entry, idx) => (
              <div 
                key={entry.employee_id} 
                className={`flex items-center p-3 rounded-lg ${idx < 3 ? 'bg-amber-50' : 'bg-slate-50'}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mr-3 ${
                  idx === 0 ? 'bg-amber-500 text-white' :
                  idx === 1 ? 'bg-slate-400 text-white' :
                  idx === 2 ? 'bg-orange-400 text-white' :
                  'bg-slate-200'
                }`}>
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{entry.employee_name}</p>
                  <p className="text-xs text-muted-foreground">{entry.employee_email}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Trophy className="w-4 h-4 text-amber-500" />
                  <span className="font-bold">{entry.badges_count}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export { BadgeCard, BadgesList, Leaderboard };

export default function PerformanceBadges({ employeeId, month, isAdmin = false }) {
  const [badges, setBadges] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBadges();
  }, [employeeId, month]);

  const fetchBadges = async () => {
    setLoading(true);
    try {
      const endpoint = employeeId 
        ? `/badges/employee/${employeeId}?period=${month}`
        : `/badges/my-badges?period=${month}`;
      const response = await api.get(endpoint);
      setBadges(response.data);
    } catch (err) {
      console.error('Error fetching badges:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluate = async () => {
    setEvaluating(true);
    try {
      if (employeeId) {
        await api.post(`/badges/evaluate/${employeeId}?month=${month}`);
      } else {
        await api.post(`/badges/evaluate-team?month=${month}`);
      }
      await fetchBadges();
    } catch (err) {
      console.error('Error evaluating badges:', err);
    } finally {
      setEvaluating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="performance-badges">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" />
            Badge-uri & Achievements
          </h2>
          <p className="text-sm text-muted-foreground">
            {badges?.unique_count || 0} badge-uri unice câștigate
          </p>
        </div>
        {isAdmin && (
          <Button onClick={handleEvaluate} disabled={evaluating}>
            {evaluating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Award className="w-4 h-4 mr-2" />
            )}
            Evaluează Badge-uri
          </Button>
        )}
      </div>

      <BadgesList badges={badges?.unique_badges || []} />

      {isAdmin && <Leaderboard month={month} />}
    </div>
  );
}
